"use client"

import styled from "styled-components"
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

const data = [
  { month: "Jan '24", value: 100 },
  { month: "Apr '24", value: 106 },
  { month: "Jul '24", value: 104 },
  { month: "Oct '24", value: 118 },
  { month: "Jan '25", value: 131 },
  { month: "Apr '25", value: 122 },
  { month: "Jul '25", value: 128 },
]

const Card = styled.section`
  border-radius: 1.5rem;
  background: var(--color-card);
  padding: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid color-mix(in oklch, var(--color-border) 60%, transparent);
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-card-foreground);
  margin: 0;
`

const Subtitle = styled.p`
  margin: 0.125rem 0 0;
  font-size: 0.875rem;
  color: var(--color-muted-foreground);
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 9999px;
  background: var(--color-optimal-muted);
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-optimal-foreground);
  white-space: nowrap;
`

const BadgeDot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: var(--color-optimal);
`

const ChartWrap = styled.div`
  margin-top: 1.5rem;
  height: 11rem;
  width: 100%;
`

const StatGrid = styled.dl`
  margin: 1.25rem 0 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  border-top: 1px solid color-mix(in oklch, var(--color-border) 70%, transparent);
  padding-top: 1rem;
`

const StatLabel = styled.dt`
  font-size: 0.75rem;
  color: var(--color-muted-foreground);
`

const StatValue = styled.dd<{ $accent?: boolean }>`
  margin: 0.25rem 0 0;
  font-family: var(--font-mono);
  font-size: 1rem;
  font-weight: 600;
  color: ${(p) =>
    p.$accent ? "var(--color-optimal-foreground)" : "var(--color-card-foreground)"};
`

const StatText = styled.dd`
  margin: 0.25rem 0 0;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-card-foreground);
`

function Dot({ cx, cy }: { cx?: number; cy?: number }) {
  if (cx == null || cy == null) return null
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill="var(--color-card)" />
      <circle cx={cx} cy={cy} r={3.5} fill="var(--color-optimal)" />
    </g>
  )
}

export default function PerformanceCard() {
  return (
    <Card>
      <Header>
        <div>
          <Title>S&amp;P 500 Index: VOO</Title>
          <Subtitle>Growth of $100 invested</Subtitle>
        </div>
        <Badge>
          <BadgeDot />
          Outperforming
        </Badge>
      </Header>

      <ChartWrap>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 12, right: 8, bottom: 0, left: -8 }}
          >
            <defs>
              <linearGradient id="etfStroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--color-optimal)" />
                <stop offset="55%" stopColor="var(--color-caution)" />
                <stop offset="80%" stopColor="var(--color-alert)" />
                <stop offset="100%" stopColor="var(--color-optimal)" />
              </linearGradient>
              <linearGradient id="etfFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-optimal)" stopOpacity={0.18} />
                <stop offset="100%" stopColor="var(--color-optimal)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
              dy={6}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[90, 140]}
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
              width={44}
              ticks={[100, 120, 140]}
            />
            <ReferenceLine
              y={100}
              stroke="var(--color-optimal)"
              strokeDasharray="4 4"
              strokeOpacity={0.5}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="none"
              fill="url(#etfFill)"
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="url(#etfStroke)"
              strokeWidth={3}
              dot={<Dot />}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartWrap>

      <StatGrid>
        <div>
          <StatLabel>Total return</StatLabel>
          <StatValue>+28.4%</StatValue>
        </div>
        <div>
          <StatLabel>Benchmark</StatLabel>
          <StatValue $accent>+0.6%</StatValue>
        </div>
        <div>
          <StatLabel>Data source</StatLabel>
          <StatText>NYSE Arca</StatText>
        </div>
      </StatGrid>
    </Card>
  )
}
